import { FormEvent, useCallback, useEffect, useState } from 'react';

import { Spinner } from '@provider-portal/common/components/Spinner/Spinner';

import { Alert, Button, Field, Input } from '@bolteu/clay-react';
import { NetworkRequestState } from '@bolteu/redux-session';

interface ForgotPasswordFormProps {
  strings: Record<'error' | 'emailSend' | 'username' | 'resetPassword', string>;
  onResetPassword: (email: string) => void;
  alertMessage: string | null;
  requestState: NetworkRequestState;
}

export function ForgotPasswordForm({
  strings,
  onResetPassword,
  alertMessage,
  requestState,
}: ForgotPasswordFormProps) {
  const [username, setUsername] = useState('');
  const [isErrorEmail, setIsErrorEmail] = useState(false);
  const [helperText, setHelperText] = useState('');
  const [shouldShowErrorAlert, setShouldShowErrorAlert] = useState(false);
  const [shouldShowSuccessAlert, setShouldShowSuccessAlert] = useState(false);

  const resetFormFieldErrors = useCallback(() => {
    setIsErrorEmail(false);
    setShouldShowErrorAlert(false);
    setHelperText('');
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onResetPassword(username);
  };

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    resetFormFieldErrors();
  };

  useEffect(() => {
    if (alertMessage) {
      setHelperText(alertMessage);
      setIsErrorEmail(true);
      return;
    }

    if (requestState === NetworkRequestState.failure) {
      setShouldShowErrorAlert(true);
      return;
    }

    if (requestState === NetworkRequestState.success) {
      setShouldShowSuccessAlert(true);
    }

    resetFormFieldErrors();
  }, [alertMessage, requestState, resetFormFieldErrors]);

  return (
    <form onSubmit={handleSubmit} data-testid="forgot-password-form">
      {shouldShowErrorAlert ? (
        <Alert.Root severity="error">
          <Alert.Icon />
          <Alert.Content>
            <Alert.Description>{strings.error}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}
      {shouldShowSuccessAlert ? (
        <Alert.Root severity="info">
          <Alert.Icon />
          <Alert.Content>
            <Alert.Description>{strings.emailSend}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}
      <Field.Root invalid={isErrorEmail} className="mt-4 mb-2">
        <Field.Label>{strings.username}</Field.Label>
        <Input
          fieldSize="lg"
          id="username"
          name="username"
          autoComplete="username"
          autoFocus
          onValueChange={handleUsernameChange}
        />
        {helperText ? <Field.Error match>{helperText}</Field.Error> : null}
      </Field.Root>
      <div className="mt-4">
        <Button type="submit" size="md" className="w-full">
          {requestState === NetworkRequestState.waiting ? (
            <Spinner />
          ) : (
            <span>{strings.resetPassword}</span>
          )}
        </Button>
      </div>
    </form>
  );
}
